import { useContext } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilCallback, useRecoilValue } from "recoil";

import {
  ApiSuffixType,
  CrudApi,
  currentUserIdSelector,
  findUserSystemManagerId,
  getDynamicEditFormRootAndMap,
  isRideFormFieldOnCard,
  Log,
  nowTDateISO,
  profileScopeAtom,
  RIDE_POST_REFERENT_FIELD_NAME,
  safeNumber,
  schemaRootAF,
  sortArrayAlphabetically,
  systemSettingsDtoAtom,
  useHasPermissionCustomField,
  useTimezone,
  type CourseRouteTypeEnum,
  type DirectionEnum,
  type IDropdownItem,
  type IDynamicEditFormRoot,
  type IRideDto,
  type IRideMetadata,
} from "@ryde/shared";

import { DropdownSourceContext } from "../../contexts";

export const useNewRideTemplate = () => {
  const { timezone } = useTimezone();
  const systemParams = useRecoilValue(systemSettingsDtoAtom);
  const scope = useRecoilValue(profileScopeAtom);
  const { getDropdownItems } = useContext(DropdownSourceContext);
  const { getFieldPermissions } = useHasPermissionCustomField();
  const allowedRideTypeIds = scope?.rideTypeIds ?? [];

  const resolveDefaultPostReferentId = useRecoilCallback(
    ({ snapshot, set }) =>
      async (): Promise<number | undefined> => {
        const currentUserId = snapshot
          .getLoadable(currentUserIdSelector)
          .valueMaybe();

        if (!currentUserId) {
          return undefined;
        }

        let rideSchemaRoot = snapshot
          .getLoadable(schemaRootAF("ride"))
          .valueMaybe();

        try {
          // The "+" outside the rides page builds the template before any drawer has fetched the ride schema.
          if (!rideSchemaRoot) {
            const schemeData = await CrudApi.getOne<IDynamicEditFormRoot>(
              "ride",
              ApiSuffixType.DynamicFormsScheme,
            );
            rideSchemaRoot = schemeData
              ? getDynamicEditFormRootAndMap(schemeData)
              : undefined;

            if (rideSchemaRoot) {
              set(schemaRootAF("ride"), rideSchemaRoot);
            }
          }

          if (
            !isRideFormFieldOnCard({
              formSchema: rideSchemaRoot?.formSchema,
              controls: rideSchemaRoot?.controls ?? [],
              fieldName: RIDE_POST_REFERENT_FIELD_NAME,
              isFieldReadable: readPermission =>
                getFieldPermissions({ readPermission }).isFieldVisible,
            })
          ) {
            return undefined;
          }

          const systemManagers = await getDropdownItems("systemManager");

          return findUserSystemManagerId({
            systemManagers,
            userId: currentUserId,
          });
        } catch (error) {
          Log.warn(
            "New ride: the post referent default could not be resolved",
            error,
          );
          return undefined;
        }
      },
    [getDropdownItems, getFieldPermissions],
  );

  const resolveDefaultRideTypeId = useEvent(
    async ({
      isJourney,
    }: {
      isJourney?: boolean;
    } = {}): Promise<number | undefined> => {
      const { defaultOrderTypeID, journeyDefaultOrderTypeID } =
        systemParams?.metadata ?? {};
      const rawDefaultRideTypeId = isJourney
        ? journeyDefaultOrderTypeID
        : defaultOrderTypeID;
      const defaultRideTypeId = safeNumber(rawDefaultRideTypeId);

      const allowedRideTypeIdsSet = allowedRideTypeIds.length
        ? new Set(allowedRideTypeIds)
        : null;

      if (
        defaultRideTypeId &&
        (!allowedRideTypeIdsSet || allowedRideTypeIdsSet.has(defaultRideTypeId))
      ) {
        return defaultRideTypeId;
      }

      const rideTypes = await getDropdownItems("rideType");
      const sortedRideTypes =
        sortArrayAlphabetically<IDropdownItem>(rideTypes || [], "label") || [];

      const fallbackRideType = sortedRideTypes.find(item => {
        const rideTypeValue = safeNumber(item.value);

        if (!rideTypeValue) {
          return false;
        }

        return (
          !allowedRideTypeIdsSet || allowedRideTypeIdsSet.has(rideTypeValue)
        );
      });

      return fallbackRideType ? safeNumber(fallbackRideType.value) : undefined;
    },
  );

  const getNewRideTemplate = useEvent(
    async ({
      metadata,
      direction,
      rideDate,
      courseId,
      courseRouteType,
      isJourney,
    }: {
      metadata?: IRideMetadata;
      direction?: DirectionEnum;
      rideDate?: TDateISO;
      courseId?: number;
      courseRouteType?: CourseRouteTypeEnum;
      isJourney?: boolean;
    } = {}): Promise<Partial<IRideDto>> => {
      const initialRideDate = rideDate || nowTDateISO(timezone);
      const additions = await getDropdownItems("addition");

      const exclusiveAdditionsIds = additions
        .filter(({ isExclusive }) => isExclusive)
        .map(({ value }) => Number(value));

      const rideTypeId = await resolveDefaultRideTypeId({ isJourney });
      const postReferentId = await resolveDefaultPostReferentId();

      const newRide: Partial<IRideDto> = {
        rideStart: initialRideDate,
        rideEnd: initialRideDate,
        direction,
        courseId,
        courseRouteType,
        rideTypeId,
        metadata: metadata || {
          stationIds: [],
          stationsInfo: [],
          passengersInfo: [],
          passengerIds: [],
        },
        computedData: {
          isNewTemplate: true,
          isDuplicate: false,
          exclusiveAdditionsIds,
        },
      };

      if (courseRouteType && newRide.metadata) {
        newRide.metadata.courseType = courseRouteType;
      }

      // Put on the template rather than set on the form, so the save sends it without the field being touched.
      if (postReferentId && newRide.metadata) {
        newRide.metadata.postReferentId ??= postReferentId;
      }

      return newRide;
    },
  );

  return { getNewRideTemplate };
};
